/**
 * One text as the card states it.
 *
 * Display-ready: the type arrives as a label, never as a message-type enum, so the card never
 * has to know a type it was not told about. The name carries the text's placement in the piece
 * (`d3_c1_title`), which is what makes a row identifiable alongside its type.
 */
export interface TextListItem {
	id: string
	/** The stored locale code, e.g. `es-es`. Displayed through {@link localeLabel}. */
	locale: string
	/** Whether this text is written in its translation group's source locale. */
	isSourceLocale: boolean
	typeLabel: string
	name: string
	content: string
}

/** Display form of a stored locale code - `es-es` is written and read as `es-ES`. */
export const localeLabel = (locale: string): string => {
	const [language, region] = locale.split('-')
	return region ? `${language?.toLowerCase()}-${region.toUpperCase()}` : locale
}

/**
 * The locales these texts are actually written in, source locales first and the translations
 * after them alphabetically - the order the reader thinks in, and it puts the tab that is
 * certain to have content in the position the card opens on.
 *
 * Derived from the texts rather than from a locale list, so the card can never offer a tab
 * with nothing behind it.
 */
export const localesOf = (texts: TextListItem[]): string[] => {
	const sourceLocales = new Set(texts.filter((text) => text.isSourceLocale).map((text) => text.locale))
	const locales = [...new Set(texts.map((text) => text.locale))]
	return locales.sort((a, b) => Number(sourceLocales.has(b)) - Number(sourceLocales.has(a)) || a.localeCompare(b))
}

/**
 * The locale in force: the reader's pick while the texts still offer it, the first one
 * otherwise. Re-scoping or filtering re-derives the tabs, so a pick made on a wider set can go
 * away under the reader - and a card with no tab selected over an empty table would read as a
 * context with no texts rather than as a locale that no longer applies.
 *
 * Derived rather than reset, as the deliverables channel is: the pick is not thrown away, so a
 * narrowing that brings the locale back honours it again.
 */
export const localeInForce = (locales: string[], picked: string | null): string | null =>
	picked !== null && locales.includes(picked) ? picked : (locales[0] ?? null)

/** The texts written in `locale`, ordered by the name that places them in the piece. */
export const textsIn = (texts: TextListItem[], locale: string | null): TextListItem[] =>
	texts
		.filter((text) => text.locale === locale)
		.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
